"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  TimeSlotGridProps,
  SelectedShowStudio,
} from "@/types/booking-new.types";
import { Show, Studio } from "@/types";
import { SLOT_STATUSES } from "@/lib/booking/booking-new/constants";
import { checkForOverlappingShows } from "@/lib/booking/booking-new/helpers";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { ROLES } from "@/lib/constants/roles";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { formatTimeDisplay } from "@/lib/utils/time";
import { BookingGapWarningModal } from "@/components/booking/BookingGapWarningModal";

export function TimeSlotGrid({
  date,
  shows,
  studios,
  timeSlotPricing,
  venueCurrency,
  venueId,
  venueDefaultLanguage,
  onSlotSelect,
  bookingType,
  customerTypes,
  groupSize,
  startTime,
  endTime,
  bookingId,
  refreshTrigger,
  setConfirmDialogOpen,
  setPendingSlotSelection,
  bookingCart,
  setBookingCart,
  setProceedWithSlotSelection,
}: TimeSlotGridProps) {
  const pathname = usePathname();

  // Determine if we're in a staff portal (superadmin, booking, or barstaff)
  const isStaffPortal =
    pathname.startsWith("/superadmin") ||
    pathname.startsWith("/booking") ||
    pathname.startsWith("/barstaff");

  // State initialization - will be populated after uniqueTimeSlots is calculated
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [announcement, setAnnouncement] = useState<string>("");
  const [bookings, setBookings] = useState<any[]>([]);
  const [allSlotData, setAllSlotData] = useState<any[]>([]); // Store ALL slot data from API
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);
  const [priceData, setPriceData] = useState<{ [key: string]: any }>({});
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [apiShows, setApiShows] = useState<Show[]>([]);
  const [commonSlotInfo, setCommonSlotInfo] = useState<{
    [key: string]: boolean;
  }>({});
  const [actuallyCommonSlots, setActuallyCommonSlots] = useState<{
    [key: string]: boolean;
  }>({});
  const [availabilityTimeSlots, setAvailabilityTimeSlots] = useState<string[]>(
    [],
  );
  const [pricingInfo, setPricingInfo] = useState<{
    role_id: number;
    type: "staff" | "guest";
    is_super_admin: boolean;
    is_staff?: boolean;
    user_role_id?: number;
  } | null>(null);

  // Gap warning modal state
  const [gapWarningOpen, setGapWarningOpen] = useState(false);
  const [gapWarningData, setGapWarningData] = useState<{
    showName: string;
    date: string;
    startTime: string;
    endTime: string;
    timeGap: number;
  } | null>(null);

  // Filter shows based on group size and customer type
  const filterShowsByGroupSize = (showsToFilter: Show[]) => {
    return showsToFilter.filter((show) => {
      // Group size compatibility
      const groupSizeMatch =
        groupSize >= show.min_participants &&
        groupSize <= show.max_participants;

      // Show-type compatibility: the road-show flow lists on_road shows; the
      // on-site flows (private/corporate) list on_site shows. A missing type is
      // treated as on_site.
      const showType = (show as any).show_type;
      const showTypeMatch =
        bookingType === "roadshow"
          ? showType === "on_road"
          : showType !== "on_road";

      // Customer type compatibility
      let customerTypeMatch = true;

      // If no customer types available, create defaults for filtering
      if (!customerTypes || customerTypes.length === 0) {
        return groupSizeMatch && showTypeMatch;
      }

      if (bookingType === "private" || bookingType === "corporate") {
        const selectedCustomerType = customerTypes.find(
          (ct) => ct.name === bookingType,
        );

        if (selectedCustomerType && show.customer_type_id) {
          customerTypeMatch =
            show.customer_type_id === selectedCustomerType.id;
        }
      }

      return groupSizeMatch && customerTypeMatch && showTypeMatch;
    });
  };

  // Apply filtering to both API shows and prop shows
  const showsToFilter = apiShows.length > 0 ? apiShows : shows;
  const baseFilteredShows = filterShowsByGroupSize(showsToFilter);


  // Ensure shows in the cart are included in filteredShows
  const cartShowIds = bookingCart.shows.map((s) => s.show.id);
  const missingShowsFromCart = bookingCart.shows
    .filter(
      (cartShow) =>
        !baseFilteredShows.find((show) => show.id === cartShow.show.id),
    )
    .map((cartShow) => cartShow.show);

  const filteredShows = [...baseFilteredShows, ...missingShowsFromCart];

  // Get unique time slots from API data or fallback to props
  const dayOfWeek = date.getDay();

  let uniqueTimeSlots: string[] = [];

  // First try to use time slots from calendar availability API
  if (availabilityTimeSlots.length > 0) {
    uniqueTimeSlots = availabilityTimeSlots;
  }
  // Fallback to API shows data if calendar availability not available
  else if (Object.keys(priceData).length > 0) {
    const allApiTimeSlots = new Set<string>();
    Object.values(priceData).forEach((showData: any) => {
      if (Array.isArray(showData)) {
        showData.forEach((slot: any) => {
          if (slot.time_slot) {
            // Convert HH:MM:SS to HH:MM format
            const timeSlot = slot.time_slot.substring(0, 5);
            allApiTimeSlots.add(timeSlot);
          }
        });
      }
    });
    uniqueTimeSlots = Array.from(allApiTimeSlots).sort();
  }

  // Fallback to timeSlotPricing if API data doesn't have time slots
  if (uniqueTimeSlots.length === 0) {
    uniqueTimeSlots = Array.from(
      new Set(
        timeSlotPricing
          .filter(
            (tp) => tp.day_of_week === dayOfWeek || tp.day_of_week === null,
          )
          .map((tp) => tp.time_slot.substring(0, 5)), // Convert HH:MM:SS to HH:MM
      ),
    ).sort();

    // If no slots found for the specific day, try without day restriction for debugging
    if (uniqueTimeSlots.length === 0) {
      uniqueTimeSlots = Array.from(
        new Set(timeSlotPricing.map((tp) => tp.time_slot.substring(0, 5))),
      ).sort();
    }
  }

  const timeSlots = uniqueTimeSlots.length > 0 ? uniqueTimeSlots : [];

  // Initialize and sync selected slots based on booking cart with duration slots
  useEffect(() => {
    const newSelectedSlots = new Set<string>();

    // Check if the current date matches any shows in the cart
    const currentDateStr = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";

    bookingCart.shows.forEach((showItem) => {
      if (showItem.date === currentDateStr) {
        // Add the main slot
        const key = `${showItem.time}-${showItem.show.id}-${showItem.studio.id}`;
        newSelectedSlots.add(key);

        // Add all duration slots
        // Don't add duration slots - they should only show duration styling
        // Duration slots will be identified dynamically using isSlotInDuration
      }
    });

    // Only update if there's a difference to avoid unnecessary re-renders
    setSelectedSlots((prevSelected) => {
      const prevArray = Array.from(prevSelected).sort();
      const newArray = Array.from(newSelectedSlots).sort();

      // Check if arrays are different
      if (
        prevArray.length !== newArray.length ||
        prevArray.some((val, index) => val !== newArray[index])
      ) {
        return newSelectedSlots;
      }

      return prevSelected;
    });
  }, [
    bookingCart.shows.length, // Simplified dependency
    JSON.stringify(
      bookingCart.shows.map((s) => ({
        showId: s.show.id,
        studioId: s.studio.id,
        time: s.time,
        date: s.date,
      })),
    ),
    date?.toISOString().split("T")[0], // Simplified date dependency
  ]);

  // Combined fetch for bookings and shows data with parallel API calls
  useEffect(() => {
    if (!date || !venueId || customerTypes.length === 0) return;

    const fetchAllData = async () => {
      setIsLoadingBookings(true);
      setIsLoadingPrices(true);

      try {
        // Format date as YYYY-MM-DD (timezone-safe)
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        // Map booking type to customer type ID
        const customerTypeId = bookingType === "corporate" ? 2 : 1; // Corporate = 2, Private = 1

        // Find the selected customer type for shows API
        let showsCustomerTypeId = 1; // Default to private (1)
        if (bookingType === "corporate") {
          const corporateType = customerTypes.find(
            (ct) => ct.name === "corporate",
          );
          showsCustomerTypeId = corporateType?.id || 2;
        } else if (bookingType === "private") {
          const privateType = customerTypes.find((ct) => ct.name === "private");
          showsCustomerTypeId = privateType?.id || 1;
        }

        // Build time-slots API query parameters
        let timeSlotsQueryParams = `date=${formattedDate}&customer_type_id=${customerTypeId}&participants=${groupSize}&show_type=${bookingType === "roadshow" ? "on_road" : "on_site"}`;

        if (startTime) {
          const timeSlotStart =
            startTime.includes(":") && startTime.split(":").length === 2
              ? `${startTime}:00`
              : startTime;
          timeSlotsQueryParams += `&timeSlotStart=${encodeURIComponent(timeSlotStart)}`;
        }
        if (endTime) {
          const timeSlotEnd =
            endTime.includes(":") && endTime.split(":").length === 2
              ? `${endTime}:00`
              : endTime;
          timeSlotsQueryParams += `&timeSlotEnd=${encodeURIComponent(timeSlotEnd)}`;
        }

        if (bookingId) {
          timeSlotsQueryParams += `&exclude_booking_id=${bookingId}`;
        }

        // Build shows API query parameters
        // Don't filter by participants - we need pricing for all shows
        // We'll filter the display based on participants instead
        const showsQueryParams = new URLSearchParams({
          venue_id: venueId,
          // Remove participant filter to get pricing for all shows
          // participants: groupSize.toString(),
          timeslot_start: startTime + ":00",
          timeslot_end: endTime + ":00",
          day_of_week: date.getDay().toString(),
          date: formattedDate,
          lang: "en",
          customer_type_id: showsCustomerTypeId.toString(),
          // Road flow lists road shows; private/corporate list on-site shows.
          show_type: bookingType === "roadshow" ? "on_road" : "on_site",
        });

        if (bookingId) {
          showsQueryParams.set("exclude_booking_id", bookingId.toString());
        }

        const showsApiEndpoint = isStaffPortal
          ? `/api/staff/venues/${venueId}/shows?${showsQueryParams}`
          : `/api/bookings/shows?${showsQueryParams}`;

        // Execute both API calls in parallel
        const [timeSlotsResponse, showsResponse] = await Promise.all([
          fetch(`/api/staff/venues/${venueId}/time-slots?${timeSlotsQueryParams}`),
          fetch(showsApiEndpoint, {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }),
        ]);

        // Process time-slots response
        if (timeSlotsResponse.ok) {
          const data = await timeSlotsResponse.json();

          if (data.success && Array.isArray(data.data)) {
            const bookedSlots: any[] = [];
            const newCommonSlotInfo: { [key: string]: boolean } = {};
            const newActuallyCommonSlots: { [key: string]: boolean } = {};

            // Extract unique time slots from the time-slots API response
            const timeSlots = data.data.map(
              (slot: any) => String(slot.time_slot).substring(0, 5),
            );
            const uniqueCalendarTimeSlots: string[] = Array.from(
              new Set<string>(timeSlots),
            ).sort();
            setAvailabilityTimeSlots(uniqueCalendarTimeSlots);

            // Store ALL slot data for reference
            setAllSlotData(data.data);

            // Process each slot detail from the new API structure
            data.data.forEach((slot: any) => {
              // Extract booked slots (check both staff and guest slots)
              if (
                slot.staff_slot?.is_booked ||
                slot.staff_slot?.is_partially_booked ||
                slot.staff_slot?.is_fully_booked
              ) {
                bookedSlots.push({
                  show_time_slot_pricing_id:
                    slot.staff_slot.show_time_slot_pricing_id,
                  start_time: slot.time_slot,
                  time_slot: slot.time_slot,
                  is_booked: slot.staff_slot.is_booked || false,
                  is_partially_booked:
                    slot.staff_slot.is_partially_booked || false,
                  is_fully_booked: slot.staff_slot.is_fully_booked || false,
                  studio_availability: slot.studio_availability,
                  available_studio_ids: slot.available_studio_ids,
                  booked_studio_ids: slot.booked_studio_ids,
                  show_assigned_studio_ids: slot.show_assigned_studio_ids,
                  status_id: slot.staff_slot.booking_status_id || null,
                  show_id: slot.show_id,
                  is_holiday_blocked: slot.is_holiday_blocked || false,
                  holiday_reason: slot.holiday_reason,
                  duration_minutes: slot.duration_minutes || null,
                });
              }
              if (
                slot.guest_slot?.is_booked ||
                slot.guest_slot?.is_partially_booked ||
                slot.guest_slot?.is_fully_booked
              ) {
                bookedSlots.push({
                  show_time_slot_pricing_id:
                    slot.guest_slot.show_time_slot_pricing_id,
                  start_time: slot.time_slot,
                  time_slot: slot.time_slot,
                  is_booked: slot.guest_slot.is_booked || false,
                  is_partially_booked:
                    slot.guest_slot.is_partially_booked || false,
                  is_fully_booked: slot.guest_slot.is_fully_booked || false,
                  studio_availability: slot.studio_availability,
                  available_studio_ids: slot.available_studio_ids,
                  booked_studio_ids: slot.booked_studio_ids,
                  show_assigned_studio_ids: slot.show_assigned_studio_ids,
                  status_id: slot.guest_slot.booking_status_id || null,
                  show_id: slot.show_id,
                  is_holiday_blocked: slot.is_holiday_blocked || false,
                  holiday_reason: slot.holiday_reason,
                  duration_minutes: slot.duration_minutes || null,
                });
              }

              // Also add slots that are holiday blocked but not booked
              if (
                slot.is_holiday_blocked &&
                !slot.staff_slot?.is_booked &&
                !slot.guest_slot?.is_booked
              ) {
                bookedSlots.push({
                  show_time_slot_pricing_id: null,
                  start_time: slot.time_slot,
                  time_slot: slot.time_slot,
                  is_booked: false,
                  is_partially_booked: false,
                  is_fully_booked: true,
                  studio_availability: slot.studio_availability,
                  available_studio_ids: slot.available_studio_ids,
                  booked_studio_ids: slot.booked_studio_ids,
                  show_assigned_studio_ids: slot.show_assigned_studio_ids,
                  status_id: null,
                  show_id: slot.show_id,
                  is_holiday_blocked: true,
                  holiday_reason: slot.holiday_reason,
                });
              }

              // Mark ALL slots for the UI with show-specific information
              if (slot.show_id) {
                const dayOfWeek = date.getDay();
                const timeWithoutSeconds = slot.time_slot.substring(0, 5);

                newCommonSlotInfo[
                  `${slot.show_id}_${dayOfWeek}_${timeWithoutSeconds}`
                ] = true;
                newCommonSlotInfo[
                  `${slot.show_id}_${dayOfWeek}_${slot.time_slot}`
                ] = true;

                if (slot.is_common) {
                  newActuallyCommonSlots[
                    `${slot.show_id}_${dayOfWeek}_${timeWithoutSeconds}`
                  ] = true;
                  newActuallyCommonSlots[
                    `${slot.show_id}_${dayOfWeek}_${slot.time_slot}`
                  ] = true;
                }
              }
            });

            setBookings(bookedSlots);
            setCommonSlotInfo(newCommonSlotInfo);
            setActuallyCommonSlots(newActuallyCommonSlots);
          } else {
            setBookings([]);
            setAllSlotData([]);
            setCommonSlotInfo({});
            setActuallyCommonSlots({});
          }
        } else {
          setBookings([]);
          setAllSlotData([]);
        }

        // Process shows response
        if (showsResponse.ok) {
          const result = await showsResponse.json();
          if (result.success && result.data) {
            const apiShows = result.data.map((item: any) => {
              const rawDuration = item.duration;
              let parsedDuration = 60;

              if (rawDuration) {
                if (typeof rawDuration === "number") {
                  parsedDuration = rawDuration;
                } else if (typeof rawDuration === "string") {
                  const simpleMinutesMatch = rawDuration.match(/^(\d+)m$/i);
                  if (simpleMinutesMatch) {
                    parsedDuration = parseInt(simpleMinutesMatch[1]);
                  } else if (rawDuration.match(/(\d+)\s*(minutes?|mins?)/i)) {
                    const minutesMatch = rawDuration.match(
                      /(\d+)\s*(minutes?|mins?)/i,
                    );
                    if (minutesMatch) {
                      parsedDuration = parseInt(minutesMatch[1]);
                    }
                  } else if (
                    rawDuration.includes("h") ||
                    rawDuration.toLowerCase().includes("hour")
                  ) {
                    const hoursMatch = rawDuration.match(
                      /(\d+)\s*(hours?|hrs?|h)/i,
                    );
                    const minsMatch = rawDuration.match(
                      /(\d+)\s*(minutes?|mins?|m)(?!onth)/i,
                    );

                    let totalMinutes = 0;
                    if (hoursMatch)
                      totalMinutes += parseInt(hoursMatch[1]) * 60;
                    if (minsMatch) totalMinutes += parseInt(minsMatch[1]);

                    if (totalMinutes > 0) parsedDuration = totalMinutes;
                  } else {
                    const digitsOnly = parseInt(
                      rawDuration.replace(/[^\\d]/g, "") || "60",
                    );
                    parsedDuration = digitsOnly;
                  }
                }
              }

              return {
                id: item.show_id,
                duration_minutes: parsedDuration,
                min_participants: item.min_participants,
                max_participants: item.max_participants,
                min_charge_participants: item.min_charge_participants || null, // Add min_charge_participants
                // Road-show base fee (shows.base_price), used to prefill the
                // cart's road-show base price.
                base_price: item.show_base_price ?? null,
                customer_type_id: showsCustomerTypeId,
                // Carry show_type so the grid's road/on-site filter works
                // (otherwise the road flow drops every show).
                show_type: item.show_type ?? null,
                status_id: 12,
                venue_id: parseInt(venueId),
                show_infos: [
                  {
                    name: item.name,
                    desc: item.description,
                    lang: item.language || "en",
                  },
                ],
                show_studios:
                  item.studios && item.studios.length > 0
                    ? item.studios.map((studio: any) => ({
                        studio_id: studio.studio_id,
                        studios: {
                          id: studio.studio_id,
                          name: studio.name,
                          capacity: studio.capacity,
                          status_id: studio.status_id,
                        },
                      }))
                    : item.show_type === "on_road"
                      ? [
                          // Road shows are studio-optional. Give a virtual
                          // placeholder studio (id 0) so the grid renders
                          // bookable cells; booking-create writes studio_id 0.
                          {
                            studio_id: 0,
                            studios: {
                              id: 0,
                              name: "No studio (road show)",
                              capacity: null,
                              status_id: 8,
                            },
                          },
                        ]
                      : [],
              };
            });

            const uniqueShows = new Map();
            apiShows.forEach((show: Show) => {
              if (!uniqueShows.has(show.id)) {
                uniqueShows.set(show.id, show);
              }
            });
            const uniqueShowsArray = Array.from(uniqueShows.values());

            setApiShows(uniqueShowsArray);

            const priceDataMap: { [key: string]: any } = {};

            result.data.forEach((item: any) => {
              if (!priceDataMap[item.show_id]) {
                priceDataMap[item.show_id] = [];
              }
              priceDataMap[item.show_id].push({
                time_slot: item.time,
                price: item.price,
                currency: item.currency,
                day_of_week: item.day_of_week,
                has_booking_conflict: item.has_booking_conflict,
                price_point_id:
                  item.price_point_id ||
                  item.show_price_point_id ||
                  item.pricing_id ||
                  null,
                show_time_slot_pricing_id:
                  item.show_time_slot_pricing_id || null,
                assigned_pricing_id: item.assigned_pricing_id || null,
                user_role_id: item.user_role_id || null,
                vat_percentage: item.vat_percentage || null,
              });
            });

            setPriceData(priceDataMap);

            if (result.pricing_info) {
              setPricingInfo(result.pricing_info);
            }
          } else {
            setApiShows([]);
            setPriceData({});
          }
        } else {
          setApiShows([]);
          setPriceData({});
        }
      } catch (error) {
        console.error("Error fetching booking data:", error);
        setBookings([]);
        setApiShows([]);
        setPriceData({});
      } finally {
        setIsLoadingBookings(false);
        setIsLoadingPrices(false);
      }
    };

    // Debounce API calls when filters change
    const timeoutId = setTimeout(() => {
      fetchAllData();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    date,
    venueId,
    bookingId,
    bookingType,
    // Only refetch for groupSize changes if cart is empty (user is still selecting)
    // If cart has items, don't refetch - use the already fetched pricing
    bookingCart.shows.length === 0 ? groupSize : null,
    startTime,
    endTime,
    customerTypes,
    refreshTrigger,
  ]);


  // Auto-reassign conflicting shows in cart to different studios
  // IMPORTANT: Only runs if there's an actual conflict (same studio at same time)
  // to prevent interfering with pre-reassignment logic
  useEffect(() => {
    if (bookingCart.shows.length < 2 || apiShows.length === 0 || !date) return;

    const currentDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    let hasChanges = false;
    const updatedShows = [...bookingCart.shows];

    // Find conflicts and reassign - prioritize reassigning EARLIER shows with multiple studios
    for (let i = 0; i < updatedShows.length; i++) {
      const currentShowInCart = updatedShows[i];

      // Only check on current date
      if (currentShowInCart.date !== currentDateStr) continue;

      // Get show data
      const currentShowData = apiShows.find((s) => s.id === currentShowInCart.show.id);
      if (!currentShowData || !currentShowData.show_studios) continue;

      const currentAssignedStudios = currentShowData.show_studios.map((ss) => ss.studios);
      const currentAssignedIds = new Set(currentAssignedStudios.map((s) => s.id));

      // Check for conflicts with other shows
      for (let j = 0; j < updatedShows.length; j++) {
        if (i === j) continue;

        const otherShowInCart = updatedShows[j];
        if (otherShowInCart.date !== currentDateStr) continue;

        const otherShowData = apiShows.find((s) => s.id === otherShowInCart.show.id);
        if (!otherShowData || !otherShowData.show_studios) continue;

        // Check for time overlap
        const [curHour, curMin] = currentShowInCart.time.split(":").map(Number);
        const curStart = curHour * 60 + curMin;
        const curEnd = curStart + (currentShowData.duration_minutes || 60);

        const [otherHour, otherMin] = otherShowInCart.time.split(":").map(Number);
        const otherStart = otherHour * 60 + otherMin;
        const otherEnd = otherStart + (otherShowData.duration_minutes || 60);

        const hasTimeOverlap =
          (curStart >= otherStart && curStart < otherEnd) ||
          (curEnd > otherStart && curEnd <= otherEnd) ||
          (curStart <= otherStart && curEnd >= otherEnd);

        if (!hasTimeOverlap) continue;

        // CRITICAL: Only proceed if they're actually using the SAME studio
        // If they're already using different studios, pre-reassignment already resolved it
        if (currentShowInCart.studio.id !== otherShowInCart.studio.id) {
          continue;
        }

        const otherAssignedIds = new Set(otherShowData.show_studios.map((ss) => ss.studios.id));
        const otherAssignedStudios = otherShowData.show_studios.map((ss) => ss.studios);

        // Strategy: Reassign whichever show has alternatives available
        // Prefer reassigning the one with more options (more flexibility)

        if (currentAssignedStudios.length > 1 && otherAssignedStudios.length === 1) {
          // Only current show has alternatives - reassign it
          const alternativeStudio = currentAssignedStudios.find(
            (s) => s.id !== currentShowInCart.studio.id && !otherAssignedIds.has(s.id)
          );

          if (alternativeStudio) {
            updatedShows[i] = {
              ...currentShowInCart,
              studio: alternativeStudio,
            };
            hasChanges = true;
            break;
          }
        } else if (otherAssignedStudios.length > 1 && currentAssignedStudios.length === 1) {
          // Only other show has alternatives - reassign it
          const alternativeStudio = otherAssignedStudios.find(
            (s) => s.id !== otherShowInCart.studio.id && !currentAssignedIds.has(s.id)
          );

          if (alternativeStudio) {
            updatedShows[j] = {
              ...otherShowInCart,
              studio: alternativeStudio,
            };
            hasChanges = true;
            break;
          }
        } else if (currentAssignedStudios.length > 1 && otherAssignedStudios.length > 1) {
          // Both have alternatives - reassign the one that appears later (recently added)
          const alternativeStudio = otherAssignedStudios.find(
            (s) => s.id !== otherShowInCart.studio.id && !currentAssignedIds.has(s.id)
          );

          if (alternativeStudio) {
            updatedShows[j] = {
              ...otherShowInCart,
              studio: alternativeStudio,
            };
            hasChanges = true;
            break;
          } else {
            // Try reassigning current instead
            const altStudio = currentAssignedStudios.find(
              (s) => s.id !== currentShowInCart.studio.id && !otherAssignedIds.has(s.id)
            );
            if (altStudio) {
              updatedShows[i] = {
                ...currentShowInCart,
                studio: altStudio,
              };
              hasChanges = true;
              break;
            }
          }
        }
      }
    }

    if (hasChanges) {
      setBookingCart({ ...bookingCart, shows: updatedShows });
    }
  }, [bookingCart.shows, apiShows, date]);

  // Forward declare this function as it's needed by getSlotStatus
  const isSlotInBookedDuration = (
    slotTime: string,
    showId: number,
    studioId: number,
  ): { inDuration: boolean; startTime?: string } => {
    const [slotHour, slotMinute] = slotTime.split(":").map(Number);
    const slotMinutes = slotHour * 60 + slotMinute;

    // Get the show details for default duration
    const show = filteredShows.find((s) => s.id === showId);
    const defaultShowDuration = show?.duration_minutes || 60;

    // First, check if there's a booking for THIS show at this exact time
    // This handles the primary booking slot
    const directBooking = bookings.find((b: any) =>
      b.show_id === showId &&
      b.time_slot.substring(0, 5) === slotTime &&
      b.is_booked &&
      !b.is_holiday_blocked
    );

    if (directBooking) {
      // This is the actual booking slot, not a duration slot
      return { inDuration: false };
    }

    // Now check for bookings that this slot falls within the duration of
    // For multi-show bookings, we need to check ALL shows at each time
    const bookingGroups = new Map<string, any[]>();
    for (const booking of bookings) {
      if (!booking.is_booked || booking.is_holiday_blocked) continue;
      const timeKey = booking.time_slot.substring(0, 5);
      if (!bookingGroups.has(timeKey)) {
        bookingGroups.set(timeKey, []);
      }
      bookingGroups.get(timeKey)!.push(booking);
    }


    // Check each booking group to see if our show is part of it
    for (const [bookingTime, bookingsAtTime] of bookingGroups) {
      // Check if this show has a booking at this time
      const ourShowBooking = bookingsAtTime.find(b => b.show_id === showId);
      if (!ourShowBooking) continue; // This show isn't part of this booking group

      const [bookedHour, bookedMinute] = bookingTime.split(":").map(Number);
      const bookedMinutes = bookedHour * 60 + bookedMinute;

      // Use the duration from our show's booking
      const bookingDuration = ourShowBooking.duration_minutes || defaultShowDuration;
      const bookedEndMinutes = bookedMinutes + bookingDuration;


      // Check if current slot falls within this booking's duration (but not the booking start itself)
      if (slotMinutes > bookedMinutes && slotMinutes < bookedEndMinutes) {
        return { inDuration: true, startTime: bookingTime };
      }
    }

    // Also need to check for conflicts with future bookings
    for (const slot of bookings) {
      // Only check slots for this show
      if (slot.show_id !== showId) {
        continue;
      }

      // IMPORTANT: Skip holiday-blocked slots - they should NOT have duration
      // Holiday blocks only block their exact time, not a duration
      if (slot.is_holiday_blocked) {
        continue;
      }

      // Check if this slot is booked (either fully booked or just booked)
      // For multi-show bookings, we need to check is_booked not just is_fully_booked
      const isBooked = slot.is_booked || slot.is_fully_booked;

      if (!isBooked) {
        continue; // Skip slots that are not booked
      }

      // Get the booked slot's start time
      const bookedTimeStr = slot.time_slot.substring(0, 5); // Convert "12:45:00" to "12:45"
      const [bookedHour, bookedMinute] = bookedTimeStr.split(":").map(Number);
      const bookedMinutes = bookedHour * 60 + bookedMinute;

      // Use custom duration from the booking if available, otherwise use default
      const bookingDuration = slot.duration_minutes || defaultShowDuration;

      // Calculate booking end time using the actual booking duration
      const bookedEndMinutes = bookedMinutes + bookingDuration;

      // Check if current slot falls within this booking's duration (but not the booking start itself)
      if (
        slotMinutes > bookedMinutes &&
        slotMinutes < bookedEndMinutes
      ) {
        return { inDuration: true, startTime: bookedTimeStr };
      }

      // ALSO check if selecting this slot would create a conflict with this future booking
      // If this slot + show duration would overlap with the booked slot
      const currentSlotEndMinutes = slotMinutes + defaultShowDuration;
      if (
        slotMinutes < bookedMinutes && // This slot starts before the booking
        currentSlotEndMinutes > bookedMinutes // But would extend into the booking time
      ) {
        // This is a CONFLICT, not a duration slot
        // Don't return inDuration: true for slots BEFORE the booking
        // Let getSlotStatus handle this as UNAVAILABLE
        continue;
      }
    }

    return { inDuration: false };
  };

  // Road-show studio availability at a given time. Road shows can run several
  // bookings at once, but each booking holds its studio/kit for its whole
  // duration, so a studio is only free at `time` if no OVERLAPPING booking of
  // this show uses it. Returns the show's assigned studios and the subset that
  // is still free. Used by both getSlotStatus (to block/allow the slot) and the
  // click handler (to place the booking on a free studio).
  const getRoadShowStudioAvailability = (
    showId: number,
    time: string,
  ): { assignedIds: number[]; freeIds: number[] } => {
    const roadSlotData = allSlotData.find(
      (s: any) => s.time_slot === `${time}:00` && s.show_id === showId,
    );
    const rsShow = filteredShows.find((s) => s.id === showId);
    const assignedIds: number[] = roadSlotData?.show_assigned_studio_ids?.length
      ? roadSlotData.show_assigned_studio_ids
      : (rsShow?.show_studios || [])
          .map((ss: any) => ss?.studios?.id ?? ss?.studio_id)
          .filter((id: any): id is number => id != null);

    if (assignedIds.length === 0) {
      return { assignedIds, freeIds: [] };
    }

    const [slotHour, slotMin] = time.split(":").map(Number);
    const slotStart = slotHour * 60 + slotMin;
    const slotDuration = rsShow?.duration_minutes || 60;
    const slotEnd = slotStart + slotDuration;

    const occupied = new Set<number>();
    for (const b of bookings) {
      if (b.show_id !== showId) continue;
      if (!b.is_booked) continue;
      if (b.status_id === STATUS_IDS.BOOKING_CANCELLED) continue;

      const [bHour, bMin] = b.time_slot.substring(0, 5).split(":").map(Number);
      const bStart = bHour * 60 + bMin;
      const bEnd = bStart + (b.duration_minutes || slotDuration);

      // Strict time-range overlap.
      if (slotStart < bEnd && slotEnd > bStart) {
        (b.booked_studio_ids || []).forEach((id: number) => occupied.add(id));
      }
    }

    return {
      assignedIds,
      freeIds: assignedIds.filter((id) => !occupied.has(id)),
    };
  };

  const getSlotStatus = (showId: number, studioId: number, time: string) => {
    // Check if the slot is holiday blocked from API data
    const timeWithSeconds = time + ":00";
    const slotDetail = bookings.find(
      (b: any) =>
        b.time_slot === timeWithSeconds &&
        b.show_id === showId &&
        b.is_holiday_blocked === true,
    );

    if (slotDetail?.is_holiday_blocked) {
      return SLOT_STATUSES.HOLIDAY_BLOCKED;
    }

    // Road shows can host several bookings at the same time — but only while a
    // studio/kit is still free. A booking occupies its studio for its whole
    // duration, so a slot is bookable iff at least one assigned studio is not
    // occupied by an OVERLAPPING booking. This is computed here (not via the
    // on-site duration/booked-slot checks below) so it works for every slot,
    // including duration slots that have no availability row of their own
    // (e.g. 09:15 during a 09:00–17:00 booking). Examples:
    //   - all 3 studios booked at 09:00 → every 09:00–17:00 slot is BOOKED
    //   - only 1 of 3 booked at 09:00  → 09:15 is AVAILABLE on the other 2
    //   - a slot after every booking ends → studios/kits free again → AVAILABLE
    if (bookingType === "roadshow") {
      const { assignedIds, freeIds } = getRoadShowStudioAvailability(
        showId,
        time,
      );
      if (assignedIds.length === 0) {
        return SLOT_STATUSES.UNAVAILABLE;
      }
      // Free studio/kit at this overlapping time → bookable.
      if (freeIds.length > 0) {
        return SLOT_STATUSES.AVAILABLE;
      }

      // No free studio/kit at this overlapping time. Rather than flag the whole
      // overlapping window as a flat "BOOKED" (which makes an 11:00–12:10
      // booking look like it starts an hour early / spans slots it doesn't own),
      // report WHY the slot is unbookable so the grid mirrors on-site shows:
      //   - the booking's actual start slot             → BOOKED
      //   - a slot inside a booking's run (after start) → DURATION_BLOCKED
      //   - a slot before a booking whose own duration
      //     would spill into it                         → INSUFFICIENT_TIME
      const [rsSlotHour, rsSlotMin] = time.split(":").map(Number);
      const rsSlotStart = rsSlotHour * 60 + rsSlotMin;
      const rsSlotDuration =
        filteredShows.find((s) => s.id === showId)?.duration_minutes || 60;

      let rsIsBookingStart = false;
      let rsInBookedDuration = false;
      for (const b of bookings) {
        if (b.show_id !== showId) continue;
        if (!b.is_booked) continue;
        if (b.status_id === STATUS_IDS.BOOKING_CANCELLED) continue;
        if (b.is_holiday_blocked) continue;

        const [bHour, bMin] = b.time_slot
          .substring(0, 5)
          .split(":")
          .map(Number);
        const bStart = bHour * 60 + bMin;
        const bEnd = bStart + (b.duration_minutes || rsSlotDuration);

        if (rsSlotStart === bStart) {
          rsIsBookingStart = true;
          break;
        }
        if (rsSlotStart > bStart && rsSlotStart < bEnd) {
          rsInBookedDuration = true;
        }
      }

      if (rsIsBookingStart) return SLOT_STATUSES.BOOKED;
      if (rsInBookedDuration) return SLOT_STATUSES.DURATION_BLOCKED;
      // Slot precedes an overlapping booking (a new show here can't finish in
      // time). Staff see the distinct "insufficient time" state; on the guest
      // side it collapses to a plain unavailable cell.
      return isStaffPortal
        ? SLOT_STATUSES.INSUFFICIENT_TIME
        : SLOT_STATUSES.UNAVAILABLE;
    }

    // EARLY CHECK: Before any availability checks, see if this is part of a booked duration
    // This prevents the slot from being marked as UNAVAILABLE when it should show duration
    const durationCheck = isSlotInBookedDuration(time, showId, studioId);
    if (durationCheck.inDuration) {
      // This slot is part of a booked show's duration
      // Return AVAILABLE so the UI can show the duration indicator properly
      return SLOT_STATUSES.AVAILABLE;
    }

    // Check if the specific studio for THIS show is available at this time
    // considering other shows' bookings and their durations
    const [currentSlotHour, currentSlotMinute] = time.split(":").map(Number);
    const currentSlotMinutes = currentSlotHour * 60 + currentSlotMinute;

    // Find the booking data for this specific show and time
    // Find the slot data from ALL slot data (not just bookings)
    const currentShowSlotData = allSlotData.find(
      (s: any) => s.time_slot === timeWithSeconds && s.show_id === showId
    );

    // Debug logging removed for production

    if (currentShowSlotData) {
      // Note: road shows are fully handled by the getRoadShowStudioAvailability
      // check above (which always returns), so bookingType is narrowed to
      // "private" | "corporate" here — the on-site studio/duration logic follows.

      // Get the studios assigned to this show
      const showAssignedStudios = currentShowSlotData.show_assigned_studio_ids || [];


      // Debug logging removed for production performance

      // If no studios are assigned to this show, it's unavailable
      if (showAssignedStudios.length === 0) {
        return SLOT_STATUSES.UNAVAILABLE;
      }

      // Collect all studios that are booked at this time (including duration checks)
      const occupiedStudios = new Set<number>();

      for (const booking of bookings) {
        // Skip if it's the same show
        if (booking.show_id === showId) continue;

        // Check if this booking is actually booked and not cancelled
        if (!booking.is_booked) continue;
        // Skip cancelled bookings
        if (booking.status_id === STATUS_IDS.BOOKING_CANCELLED) continue;

        // Get the booking time
        const bookingTime = booking.time_slot.substring(0, 5); // Remove seconds
        const [bookingHour, bookingMinute] = bookingTime.split(":").map(Number);
        const bookingMinutes = bookingHour * 60 + bookingMinute;

        // Get the booked show's duration (use custom duration if available)
        const bookedShow = filteredShows.find((s) => s.id === booking.show_id);
        const bookedShowDuration = booking.duration_minutes || bookedShow?.duration_minutes || 60;

        // Check if current slot falls within the other show's booking duration
        if (currentSlotMinutes >= bookingMinutes && currentSlotMinutes < bookingMinutes + bookedShowDuration) {
          // Add the booked studio IDs to occupied set
          if (booking.booked_studio_ids && Array.isArray(booking.booked_studio_ids)) {
            booking.booked_studio_ids.forEach((sid: number) => occupiedStudios.add(sid));
          }

          // Debug logging removed for production performance
        }
      }

      // First check if THIS show actually has a booking at this time
      const isActuallyBooked = currentShowSlotData.staff_slot?.is_booked || currentShowSlotData.guest_slot?.is_booked;

      // Also check if there's any booking for this show at this specific time
      // This handles multi-show bookings where the API might not mark all shows as booked
      const hasDirectBooking = bookings.some((b: any) =>
        b.show_id === showId &&
        b.time_slot === timeWithSeconds &&
        b.is_booked &&
        b.status_id !== STATUS_IDS.BOOKING_CANCELLED &&
        !b.is_holiday_blocked
      );

      if (isActuallyBooked || hasDirectBooking) {
        // This show has an actual booking at this time
        return SLOT_STATUSES.BOOKED;
      }

      // Check if studios are available considering duration of other bookings
      // If all assigned studios are occupied (including by bookings with duration), slot is unavailable
      if (showAssignedStudios.length > 0 && occupiedStudios.size > 0) {
        const allStudiosOccupied = showAssignedStudios.every((studioId: number) => occupiedStudios.has(studioId));
        if (allStudiosOccupied) {
          return SLOT_STATUSES.UNAVAILABLE;
        }
      }

      // Check if selecting this slot would overlap with future bookings
      // Get the current show's duration
      const currentShow = filteredShows.find((s) => s.id === showId);
      const currentShowDuration = currentShow?.duration_minutes || 60;


      // For multi-show bookings, we need to collect ALL studios that are booked at each time
      // Group bookings by time slot
      const bookingsByTime = new Map<string, Set<number>>();
      for (const booking of bookings) {
        if (!booking.is_booked) continue;
        if (booking.status_id === STATUS_IDS.BOOKING_CANCELLED) continue;
        if (booking.is_holiday_blocked) continue;

        const bookingTime = booking.time_slot.substring(0, 5);
        if (!bookingsByTime.has(bookingTime)) {
          bookingsByTime.set(bookingTime, new Set<number>());
        }
        if (booking.booked_studio_ids && booking.booked_studio_ids.length > 0) {
          booking.booked_studio_ids.forEach((studioId: number) => {
            bookingsByTime.get(bookingTime)!.add(studioId);
          });
        }
      }

      // Check if this slot's duration would overlap with any future bookings (including same show)
      for (const booking of bookings) {
        // Skip if not booked or cancelled
        if (!booking.is_booked) continue;
        if (booking.status_id === STATUS_IDS.BOOKING_CANCELLED) continue;
        // Skip holiday blocks - they don't have duration
        if (booking.is_holiday_blocked) continue;

        // Get the future booking time
        const futureBookingTime = booking.time_slot.substring(0, 5);
        const [futureHour, futureMinute] = futureBookingTime.split(":").map(Number);
        const futureMinutes = futureHour * 60 + futureMinute;

        // Check if current slot + its duration would overlap with the future booking
        if (currentSlotMinutes < futureMinutes && currentSlotMinutes + currentShowDuration > futureMinutes) {
          // Get ALL studios booked at the future time (across all shows)
          const allBookedStudiosAtFutureTime = bookingsByTime.get(futureBookingTime) || new Set<number>();


          // For multi-show bookings, we need to check if ANY studio at the future time would conflict
          // For the same show, it's always a conflict if any studio is taken
          if (booking.show_id === showId) {

            // For same show conflicts, check if the show has only one studio total
            // If it's a single-studio show, ANY booking blocks it regardless of which studio
            const showData = filteredShows.find(s => s.id === showId);
            const totalShowStudios = showData?.show_studios?.length || 0;

            if (totalShowStudios === 1) {
              // Single studio show - if there's ANY booking for this show at the future time, block it
              // Use INSUFFICIENT_TIME for staff portals, UNAVAILABLE for customer portal
              return isStaffPortal ? SLOT_STATUSES.INSUFFICIENT_TIME : SLOT_STATUSES.UNAVAILABLE;
            }

            // Multi-studio show - check if specific studios conflict
            if (!booking.booked_studio_ids || booking.booked_studio_ids.length === 0) {
              // No studio info means we assume all studios are booked
              return isStaffPortal ? SLOT_STATUSES.INSUFFICIENT_TIME : SLOT_STATUSES.UNAVAILABLE;
            }

            if (showAssignedStudios.length > 0) {
              // Check against ALL studios booked at the future time, not just this one booking
              const blockedStudios = showAssignedStudios.filter((studioId: number) =>
                allBookedStudiosAtFutureTime.has(studioId)
              );
              // Only mark as unavailable if ALL studios would be blocked
              if (blockedStudios.length === showAssignedStudios.length) {
                return isStaffPortal ? SLOT_STATUSES.INSUFFICIENT_TIME : SLOT_STATUSES.UNAVAILABLE;
              }
            }
          } else {
            // Different show - check if studios conflict
            // In a multi-show booking scenario, if the future booking uses ANY studio
            // that this show needs, and this show would extend into that time, it's a conflict

            if (!booking.booked_studio_ids || booking.booked_studio_ids.length === 0) {
              // No studio info - can't determine conflict, allow it
              // (This is different from same-show where we block by default)
            } else if (showAssignedStudios.length > 0) {
              // Check against ALL studios booked at the future time
              const blockedStudios = showAssignedStudios.filter((studioId: number) =>
                allBookedStudiosAtFutureTime.has(studioId)
              );

              // If ALL studios this show needs would be blocked, mark as unavailable
              if (blockedStudios.length === showAssignedStudios.length) {
                return isStaffPortal ? SLOT_STATUSES.INSUFFICIENT_TIME : SLOT_STATUSES.UNAVAILABLE;
              }
            }
          }
        }
      }

      // Simple check: if available_studio_ids is empty, slot is unavailable
      if (currentShowSlotData.available_studio_ids && currentShowSlotData.available_studio_ids.length === 0) {
        // Check if the unavailability is due to holiday overlap (insufficient time)
        // The API marks slots affected by holidays with holiday_reason
        if (currentShowSlotData.holiday_reason && !currentShowSlotData.is_holiday_blocked) {
          // This slot would overlap with a holiday block due to show duration
          return isStaffPortal ? SLOT_STATUSES.INSUFFICIENT_TIME : SLOT_STATUSES.UNAVAILABLE;
        }
        // No studios available and this show hasn't booked it = UNAVAILABLE
        return SLOT_STATUSES.UNAVAILABLE;
      }
    } else {
      // No slot data found
    }

    // When editing a booking (bookingId present), treat that booking's slots as available
    // This allows staff to modify/remove slots from the current booking
    if (bookingId) {
      // Check if this slot is part of the current booking being edited
      // If it's in the cart, it's already handled by selectedSlots
      // If it's NOT in the cart but was in the original booking, treat as available
      const currentDateStr = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : "";

      const isInCurrentCart = bookingCart.shows.some(
        (s) =>
          s.show.id === showId && s.time === time && s.date === currentDateStr,
      );

      // If not in cart, this could be a slot we just unselected from the booking
      // Don't show it as booked since we're editing this booking
      if (!isInCurrentCart) {
        // Continue to check if it's booked by OTHER bookings below
      }
    }

    // First check if this slot is part of a booked show's duration
    const [slotHour, slotMinute] = time.split(":").map(Number);
    const slotMinutes = slotHour * 60 + slotMinute;

    // Get the show details for duration
    const show = filteredShows.find((s) => s.id === showId);
    const showDuration = show?.duration_minutes || 60;

    // Check all bookings to see if this slot falls within their duration
    for (const booking of bookings) {
      // Get booking start time
      const bookingTime = booking.time_slot.substring(0, 5); // Remove seconds
      const [bookingHour, bookingMinute] = bookingTime.split(":").map(Number);
      const bookingMinutes = bookingHour * 60 + bookingMinute;

      // Get the booked show ID and duration from the booking data directly
      const bookedShowId = booking.show_id;
      const bookedShow = filteredShows.find((s) => s.id === bookedShowId);
      // Use custom duration from booking if available, otherwise use show's default
      const bookedShowDuration = booking.duration_minutes || bookedShow?.duration_minutes || 60;

      // Check if current slot falls within the booking's duration (for any show)
      if (
        slotMinutes >= bookingMinutes &&
        slotMinutes < bookingMinutes + bookedShowDuration
      ) {
        // If it's the exact booking time AND same show, show booking status
        if (slotMinutes === bookingMinutes && bookedShowId === showId) {
          // Check if THIS show actually has a booking at this time
          const isActuallyBooked = booking.staff_slot?.is_booked || booking.guest_slot?.is_booked;

          if (isActuallyBooked) {
            // This show has an actual booking at this time
            // Check for preliminary booking (BOOKING_PRELIMINARY status)
            if (booking.staff_slot?.booking_status_id === STATUS_IDS.BOOKING_PRELIMINARY ||
                booking.guest_slot?.booking_status_id === STATUS_IDS.BOOKING_PRELIMINARY) {
              return SLOT_STATUSES.PRELIMINARY;
            }
            // Check for confirmed/reserved booking (BOOKING_CONFIRMED status)
            else if (booking.staff_slot?.booking_status_id === STATUS_IDS.BOOKING_CONFIRMED ||
                     booking.guest_slot?.booking_status_id === STATUS_IDS.BOOKING_CONFIRMED) {
              return SLOT_STATUSES.BOOKED;
            } else {
              return SLOT_STATUSES.BOOKED;
            }
          }
          // If not booked but fully booked (no studios available), it will be caught
          // by the UNAVAILABLE check earlier in the function
        }
        // For slots within the booking's duration (but not the exact booking start for this show),
        // mark as DURATION_BLOCKED to prevent selection while appearing as available initially
        // IMPORTANT: Don't mark as DURATION_BLOCKED if the booking itself is holiday-blocked
        if (bookedShowId === showId && slotMinutes > bookingMinutes) {
          // Check if the booking's start time is holiday-blocked
          const bookingStartTime = booking.time_slot;
          const bookingIsHolidayBlocked = booking.is_holiday_blocked === true;

          // If the booking is holiday-blocked, don't mark duration slots as blocked
          if (!bookingIsHolidayBlocked) {
            return SLOT_STATUSES.DURATION_BLOCKED;
          }
        }
      }
    }

    // After duration check, check API data for booking conflicts (actual sold out situations)
    const showPriceData = priceData[showId];
    if (showPriceData && Array.isArray(showPriceData)) {
      const slot = showPriceData.find((p: any) => p.time_slot === time + ":00");

      // Check if slot is marked as sold out (has booking conflict)
      // Note: API should already exclude current booking (via exclude_booking_id parameter)
      // Only return SOLDOUT if it's not already identified as part of a duration above
      if (slot && slot.has_booking_conflict) {
        return SLOT_STATUSES.SOLDOUT;
      }
    }

    // Check if there's a booking for this specific show at this time slot
    const dayOfWeek = date.getDay();

    // Find the pricing ID for this show at this time
    const pricingSlot = timeSlotPricing.find(
      (tp) =>
        tp.show_id === showId &&
        tp.time_slot === timeWithSeconds &&
        (tp.day_of_week === dayOfWeek || tp.day_of_week === null),
    );

    if (pricingSlot) {
      // Check if there's a booking with this specific pricing ID
      const booking = bookings.find(
        (b) => b.show_time_slot_pricing_id === pricingSlot.id && b.is_booked,
      );

      if (booking) {
        // If fully booked, no studios available - show BOOKED
        if (booking.is_fully_booked) {
          return SLOT_STATUSES.BOOKED;
        }
        // If partially booked, some studios available - show PARTIAL
        if (booking.is_partially_booked) {
          return SLOT_STATUSES.PARTIAL;
        }
        // Check for preliminary booking (BOOKING_PRELIMINARY status)
        if (booking.status_id === STATUS_IDS.BOOKING_PRELIMINARY) {
          return SLOT_STATUSES.PRELIMINARY; // New status for preliminary bookings
        }
        // Check for confirmed/reserved booking (BOOKING_CONFIRMED status)
        else if (booking.status_id === STATUS_IDS.BOOKING_CONFIRMED) {
          return SLOT_STATUSES.BOOKED;
        } else {
          // Default to available if partially booked or status unclear
          return SLOT_STATUSES.PARTIAL;
        }
      }
    }

    // Final fallback - should be available
    return SLOT_STATUSES.AVAILABLE;
  };

  const getSlotPrice = (showId: number, time: string): number | null => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // First check if we have pricing data from the API
    const showPriceData = priceData[showId];

    if (showPriceData && Array.isArray(showPriceData)) {
      const pricing = showPriceData.find((p: any) => {
        // Handle both HH:MM and HH:MM:SS formats
        const normalizedApiTime = p.time_slot.substring(0, 5); // Get just HH:MM
        return (
          normalizedApiTime === time &&
          (p.day_of_week === dayOfWeek || p.day_of_week === null)
        );
      });

      if (pricing && pricing.price !== undefined) {
        return pricing.price;
      }
    }

    // Fallback to timeSlotPricing prop
    const slotPricing = timeSlotPricing.find(
      (tp) =>
        tp.show_id === showId &&
        tp.time_slot.startsWith(time) && // Match HH:MM with HH:MM:SS
        (tp.day_of_week === dayOfWeek || tp.day_of_week === null),
    );

    if (
      slotPricing &&
      slotPricing.show_price_points &&
      Array.isArray(slotPricing.show_price_points) &&
      slotPricing.show_price_points.length > 0
    ) {
      const pricePoint = slotPricing.show_price_points[0];
      if (pricePoint.pricing && pricePoint.pricing.length > 0) {
        return pricePoint.pricing[0].price;
      }
    }

    return null; // No price found
  };

  // Check if a time slot is available (for display purposes)
  const isCommonTimeSlot = (showId: number, time: string): boolean => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Check the commonSlotInfo state that was populated from API (shows ALL available slots)
    const key = `${showId}_${dayOfWeek}_${time}`;
    const keyWithSeconds = `${showId}_${dayOfWeek}_${time}:00`;

    return !!(commonSlotInfo[key] || commonSlotInfo[keyWithSeconds]);
  };

  // Check if a time slot is actually common (available for both staff and guest roles)
  const isActuallyCommon = (showId: number, time: string): boolean => {
    const dayOfWeek = date.getDay();

    const key = `${showId}_${dayOfWeek}_${time}`;
    const keyWithSeconds = `${showId}_${dayOfWeek}_${time}:00`;

    return !!(actuallyCommonSlots[key] || actuallyCommonSlots[keyWithSeconds]);
  };

  // Helper function to check if a time slot is part of a selected show's duration
  const isSlotInDuration = (
    slotTime: string,
    showId: number,
    studioId: number,
  ): boolean => {
    const [slotHour, slotMinute] = slotTime.split(":").map(Number);
    const slotMinutes = slotHour * 60 + slotMinute;

    // Check if this exact slot is already selected (don't mark as duration if it's a primary selection)
    const exactSlotKey = `${slotTime}-${showId}-${studioId}`;
    if (selectedSlots.has(exactSlotKey)) {
      return false; // This is a primary selection, not a duration slot
    }

    // Check ALL shows in the cart to see if this slot falls within any show's duration
    // Duration slots are shown for a show regardless of which studio it's displayed in
    // We only care about: same date, same show ID (not studio ID, because show might have been reassigned)
    const currentDateStr = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";

    for (const showInCart of bookingCart.shows) {
      // Only check shows on the same date and same show ID
      // Duration spans the entire show regardless of which studio column it appears in
      if (
        showInCart.date === currentDateStr &&
        showInCart.show.id === showId
      ) {
        const [startHour, startMinute] = showInCart.time.split(":").map(Number);
        const startMinutes = startHour * 60 + startMinute;

        // IMPORTANT: Check if the show's start time is holiday-blocked
        // If it is, we should NOT mark duration slots as blocked
        const startTimeWithSeconds = showInCart.time + ":00";
        const startSlotData = bookings.find(
          (b: any) =>
            b.time_slot === startTimeWithSeconds &&
            b.show_id === showId &&
            b.is_holiday_blocked === true
        );

        // If the show's start time is holiday-blocked, skip duration checking
        // Holiday blocks should only block their exact time, not duration
        if (startSlotData?.is_holiday_blocked) {
          continue; // Skip this show - it's holiday-blocked so shouldn't block duration slots
        }

        // Calculate duration based on customEndTime if available
        let duration = showInCart.duration_minutes || showInCart.show.duration_minutes || 60;
        if (showInCart.customEndTime) {
          const [endHour, endMin] = showInCart.customEndTime.split(":").map(Number);
          const endMinutes = endHour * 60 + endMin;
          const calculatedDuration = endMinutes - startMinutes;
          // Only use custom duration if it's valid (positive)
          if (calculatedDuration > 0) {
            duration = calculatedDuration;
          }
        }

        // Check if current slot falls within the duration of this show
        if (
          slotMinutes > startMinutes &&
          slotMinutes < startMinutes + duration
        ) {
          return true; // This slot is part of a show's duration
        }
      }
    }

    return false;
  };


  // Get the start time of a show if this slot is part of its duration
  const getShowStartTime = (
    slotTime: string,
    showId: number,
    studioId: number,
  ): string | null => {
    const currentDateStr = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";

    // Check booking cart shows (which have the correct duration)
    for (const showInCart of bookingCart.shows) {
      if (showInCart.date === currentDateStr && showInCart.show.id === showId) {
        const [slotHour, slotMinute] = slotTime.split(":").map(Number);
        const slotMinutes = slotHour * 60 + slotMinute;

        // Normalize showInCart.time to HH:MM format (remove seconds if present)
        const cartTimeNormalized = showInCart.time.length > 5 ? showInCart.time.substring(0, 5) : showInCart.time;
        const [startHour, startMinute] = cartTimeNormalized.split(":").map(Number);
        const startMinutes = startHour * 60 + startMinute;
        const duration =
          showInCart.duration_minutes || showInCart.show.duration_minutes || 60;

        if (
          slotMinutes >= startMinutes &&
          slotMinutes < startMinutes + duration
        ) {
          return cartTimeNormalized;
        }
      }
    }

    // Fallback to selected slots - iterate through selectedSlots to find matching show time
    let foundShowTime: string | null = null;
    for (const selected of selectedSlots) {
      const [selectedTime, selectedShowId, selectedStudioId] =
        selected.split("-");

      if (selectedShowId !== showId.toString()) continue;

      // Keep track of the earliest time for this show (the start time)
      if (!foundShowTime || selectedTime < foundShowTime) {
        foundShowTime = selectedTime;
      }
    }

    if (foundShowTime) {
      // Verify this slot actually falls within the duration
      const [slotHour, slotMinute] = slotTime.split(":").map(Number);
      const slotMinutes = slotHour * 60 + slotMinute;
      const [startHour, startMinute] = foundShowTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMinute;

      const show = filteredShows.find((s) => s.id === showId);
      const duration = show?.duration_minutes || 60;

      if (slotMinutes >= startMinutes && slotMinutes < startMinutes + duration) {
        return foundShowTime;
      }
    }

    return null;
  };

  const handleSlotClick = async (
    show: Show,
    originalStudio: Studio,
    time: string,
    status: string,
    availableStudiosFromApi: any[] | null = null,
  ) => {
    // GAP CHECK - FIRST PRIORITY (before all other checks)
    // Only check for new selections, not deselections
    const isDeselecting = selectedSlots.has(`${time}-${show.id}-${originalStudio.id}`);

    // The 15-minute rule is for venue studios; road shows run off-site.
    if (!isDeselecting && bookingType !== "roadshow") {
      try {
        const dateStr = date
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
          : "";

        const studioIdToSend = originalStudio?.id;

        // Get all assigned studios for this show
        const assignedStudioIds = show.show_studios?.map((ss) => ss.studios?.id).filter(Boolean) || [];

        const requestPayload = {
          showDate: dateStr,
          startTime: time,
          duration: show.duration_minutes || 60,
          venueId: parseInt(venueId as string),
          studioId: studioIdToSend,
          assignedStudioIds: assignedStudioIds.length > 0 ? assignedStudioIds : undefined,
          isStaffRequest: true, // Explicitly mark as staff request
          excludeBookingId: bookingId || undefined, // Exclude current booking in edit mode
        };

        const gapCheckResponse = await fetch(
          "/api/bookings/check-gap",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestPayload),
          }
        );

        if (gapCheckResponse.ok) {
          const gapCheckResult = await gapCheckResponse.json();

          if (gapCheckResult.hasConflict) {
            // Hard stop: shows need 15 minutes between them, no override.
            setGapWarningData(gapCheckResult.conflictingBooking);
            setGapWarningOpen(true);
            return;
          }
        } else {
          toast.error("Failed to check booking availability", { duration: 5000 });
          return;
        }
      } catch (error) {
        toast.error("Error checking booking availability", { duration: 5000 });
        return;
      }
    }

    let studio = originalStudio; // Allow studio reassignment
    let key = `${time}-${show.id}-${studio.id}`;
    let conflictResolved = false; // Declare here so it's accessible throughout function

    // Allow deselection even if the slot is booked/reserved/soldout
    if (!isDeselecting) {
      if (
        status === SLOT_STATUSES.BOOKED ||
        status === SLOT_STATUSES.PRELIMINARY ||
        status === SLOT_STATUSES.SOLDOUT ||
        status === SLOT_STATUSES.DURATION_BLOCKED ||
        status === SLOT_STATUSES.HOLIDAY_BLOCKED
      ) {
        return;
      }

      // Don't allow clicking on duration slots (on-site only). Road shows are
      // concurrent — a prior booking's duration must not block a new one.
      const inDuration =
        bookingType === "roadshow"
          ? false
          : isSlotInDuration(time, show.id, studio.id);
      if (inDuration) {
        return;
      }

      const price = getSlotPrice(show.id, time);
      if (price === null) {
        toast.error("Cannot book this slot - price information is unavailable");
        return;
      }
    }

    // Check for overlapping shows when selecting (not deselecting)
    if (!isDeselecting) {
      // First, try to intelligently resolve conflicts by reassigning existing shows
      const currentDateStr = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : "";

      // Check if new show would conflict with existing cart shows
      const newShowAssignedStudios = show.show_studios?.map((ss) => ss.studios) || [];
      const newShowAssignedIds = new Set(newShowAssignedStudios.map((s) => s.id));


      for (const cartShow of bookingCart.shows) {
        if (cartShow.date !== currentDateStr || cartShow.show.id === show.id) continue;

        // Check time overlap
        const [newHour, newMin] = time.split(":").map(Number);
        const newStart = newHour * 60 + newMin;
        const newEnd = newStart + (show.duration_minutes || 60);

        const [cartHour, cartMin] = cartShow.time.split(":").map(Number);
        const cartStart = cartHour * 60 + cartMin;
        const cartEnd = cartStart + (cartShow.show.duration_minutes || 60);

        const hasTimeOverlap =
          (newStart >= cartStart && newStart < cartEnd) ||
          (newEnd > cartStart && newEnd <= cartEnd) ||
          (newStart <= cartStart && newEnd >= cartEnd);

        if (!hasTimeOverlap) continue;


        // Time overlaps - check if they'd use the same studio
        if (cartShow.studio.id && newShowAssignedIds.has(cartShow.studio.id)) {
          // Conflict detected: new show wants cartShow's studio
          // Try to reassign cartShow to an alternative
          const cartShowAssignedStudios = cartShow.show.show_studios?.map((ss) => ss.studios) || [];
          const cartShowAssignedIds = new Set(cartShowAssignedStudios.map((s) => s.id));
          const newShowAssignedIds2 = new Set(newShowAssignedStudios.map((s) => s.id));

          if (cartShowAssignedStudios.length > 1) {
            // CartShow has alternatives - find one not used by new show
            const alternativeStudio = cartShowAssignedStudios.find(
              (s) => s.id !== cartShow.studio.id && !newShowAssignedIds2.has(s.id)
            );

            if (alternativeStudio) {
              // Found alternative - update cart show's studio
              const updatedCart = bookingCart.shows.map((s) =>
                s.show.id === cartShow.show.id && s.time === cartShow.time
                  ? { ...s, studio: alternativeStudio }
                  : s
              );

              // UPDATE CART IMMEDIATELY - Use updatedCart for subsequent operations
              // (Don't rely on async setBookingCart to propagate in time)
              const newBookingCart = { ...bookingCart, shows: updatedCart };
              setBookingCart(newBookingCart);

              toast.success(
                `Made room for this booking by reassigning "${cartShow.show.show_infos?.[0]?.name || "Show " + cartShow.show.id}" to ${alternativeStudio.name}`,
                { duration: 6000 },
              );

              // Now proceed with adding this show using its first assigned studio
              const assignedStudio = newShowAssignedStudios[0];
              if (assignedStudio) {
                const foundStudio = studios.find((s) => s.id === assignedStudio.id);
                if (foundStudio) {
                  studio = foundStudio;
                  key = `${time}-${show.id}-${studio.id}`;
                  conflictResolved = true;
                } else {
                  console.warn(`Could not find studio object for studio ID ${assignedStudio.id}. Available studios:`, studios.map(s => `${s.id}:${s.name}`));
                }
              }
              break;
            }
          } else {
            // CartShow has NO alternatives and we can't move it
            // NEW SHOW also has conflict - check if new show has alternatives
            if (newShowAssignedStudios.length === 1) {
              // New show has NO alternatives either - CANNOT ADD
              toast.error(
                `Cannot book this time slot. "${cartShow.show.show_infos?.[0]?.name || "Show " + cartShow.show.id}" is already booked in the only available studio for "${show.show_infos?.[0]?.name || "Show " + show.id}".`,
                { duration: 6000 }
              );
              return;
            }
          }
        }
      }


      // If conflict was already resolved by pre-reassignment, skip further conflict checking
      if (conflictResolved) {
        // Proceed directly to adding the show - don't run additional conflict checks
      } else {

      // First priority: use API availability data if provided
      let availableStudios = availableStudiosFromApi || [];

      // Fallback to cart-based conflict checking if API data not available
      if (!availableStudiosFromApi) {
        const overlapCheck = checkForOverlappingShows(
          show,
          time,
          studio.id,
          bookingCart.shows,
          studios,
          groupSize,
          date,
        );
        if (overlapCheck.hasOverlap && overlapCheck.overlappingShow) {
          const overlapping = overlapCheck.overlappingShow;
          availableStudios = overlapCheck.availableStudios || [];

          if (availableStudios.length > 0) {
            // Automatically assign the first available studio
            const autoAssignedStudio = availableStudios[0];

            // Show success message about auto-assignment
            toast.success(
              `Studio conflict resolved! "${overlapping.show.show_infos?.[0]?.name || "Show " + overlapping.show.id}" is in ${overlapping.studio.name}, so we've assigned your booking to ${autoAssignedStudio.name}`,
              { duration: 6000 },
            );

            // Continue with the auto-assigned studio instead of the originally selected one
            studio = autoAssignedStudio;
            // Update the key to use the new studio ID
            key = `${time}-${show.id}-${studio.id}`;
          } else {
            // No available studios - show detailed error
            const totalStudios = studios.length;
            const occupiedCount = Array.from(
              new Set(
                bookingCart.shows
                  .filter(
                    (s) =>
                      s.date ===
                      (date
                        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                        : ""),
                  )
                  .map((s) => s.studio.id),
              ),
            ).length;
            const tooSmallStudios = studios.filter(
              (s) => s.capacity < groupSize,
            ).length;

            let message = `This show would overlap with "${overlapping.show.show_infos?.[0]?.name || "Show " + overlapping.show.id}" in ${overlapping.studio.name} from ${overlapping.startTime} to ${overlapping.endTime}. No other studios available`;

            if (tooSmallStudios > 0) {
              message += ` (${tooSmallStudios} too small for group of ${groupSize})`;
            }
            if (occupiedCount > 1) {
              message += ` (${occupiedCount} studios occupied)`;
            }
            message += ".";

            toast.error(message, { duration: 7000 });
            return;
          }
        }
      } else if (availableStudiosFromApi) {
        // API data provided: use it for smart studio assignment
        if (availableStudios.length > 0) {
          if (studio.id && !availableStudios.find((s) => s.id === studio.id)) {
            // Selected studio is not available, assign to first available
            const autoAssignedStudio = availableStudios[0];

            // Check if it's an assigned studio or if we're assigning from all available
            const showAssignedStudioIds = new Set(
              show.show_studios?.map((ss) => ss.studios.id) || []
            );
            const isAssignedStudio = showAssignedStudioIds.has(autoAssignedStudio.id);

            if (isAssignedStudio) {
              toast.success(
                `Studio ${studio.name} is not available. Assigned to ${autoAssignedStudio.name} instead.`,
                { duration: 6000 },
              );
            } else {
              // Assigning to a studio not in this show's assignment - will trigger auto-reassignment
              toast.info(
                `Booking in ${autoAssignedStudio.name}. If there's a conflict, other shows will be reassigned automatically.`,
                { duration: 6000 },
              );
            }

            studio = autoAssignedStudio;
            key = `${time}-${show.id}-${studio.id}`;
          }
        }
        // If no available studios at all, we already blocked in onClick, so we won't reach here
      }
      } // Close the else block
    }

    // Check if this is a non-recommended slot (not actually common)
    const isCommon = isActuallyCommon(show.id, time);

    try {
      // For deselection, we don't need price
      const slotPrice = isDeselecting ? null : getSlotPrice(show.id, time);


      // If selecting a non-recommended slot, show confirmation dialog
      if (!isCommon && !isDeselecting) {
        try {
          setPendingSlotSelection({ show, studio, time, price: slotPrice });
          setConfirmDialogOpen(true);
          // Store the callback function for later use
          if (setProceedWithSlotSelection) {
            setProceedWithSlotSelection(
              () => () => proceedWithSlotSelection(show, studio, time, slotPrice),
            );
          }
          return;
        } catch (error) {
          console.error(`Error setting confirmation dialog:`, error);
        }
      }

      // Otherwise, proceed with the selection/deselection
      proceedWithSlotSelection(show, studio, time, slotPrice, availableStudiosFromApi);
    } catch (error) {
      console.error(`Error in handleSlotClick after isDeselecting check:`, error);
    }
  };

  const proceedWithSlotSelection = (
    show: Show,
    studio: Studio,
    time: string,
    price: number | null,
    availableStudiosFromApi: any[] | null = null,
  ) => {
    const key = `${time}-${show.id}-${studio.id}`;
    const newSelected = new Set(selectedSlots);
    const isDeselecting = selectedSlots.has(key);
    const showName =
      show.show_infos?.find((info) => info.lang === "en")?.name ||
      show.show_infos?.[0]?.name ||
      `Show ${show.id}`;

    if (isDeselecting) {
      // When deselecting, remove all slots that were part of this show's duration
      const [selectedHour, selectedMinute] = time.split(":").map(Number);
      const selectedStartMinutes = selectedHour * 60 + selectedMinute;
      const duration = show.duration_minutes || 60;

      // Remove the main slot and all duration slots
      Array.from(newSelected).forEach((slot) => {
        const [slotTime, slotShowId, slotStudioId] = slot.split("-");
        if (
          slotShowId === show.id.toString() &&
          slotStudioId === studio.id.toString()
        ) {
          const [slotHour, slotMinute] = slotTime.split(":").map(Number);
          const slotMinutes = slotHour * 60 + slotMinute;

          // Check if this slot falls within the duration of the selected show
          // Include the start slot and all duration slots
          if (
            slotMinutes >= selectedStartMinutes &&
            slotMinutes < selectedStartMinutes + duration
          ) {
            newSelected.delete(slot);
          }
        }
      });

      // Remove the show from the cart when deselecting
      const selectedDate = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : "";

      // Update selected slots first
      setSelectedSlots(newSelected);

      // Then remove from cart
      setBookingCart((prev) => {
        const filtered = prev.shows.filter((s) => {
          const shouldKeep = !(
            s.show.id === show.id &&
            s.date === selectedDate &&
            s.time === time
          );
          return shouldKeep;
        });

        return {
          ...prev,
          shows: filtered,
        };
      });

      setAnnouncement(`${showName} at ${time} has been deselected`);
      toast.success(`${showName} removed from cart`);
      // Exit early to prevent calling onSlotSelect
      return;
    } else {
      // When selecting, only add the start slot

      // In edit mode, if selecting a new time that overlaps with existing booking, remove the old one
      if (bookingId && bookingCart.shows.length > 0) {
        const [newHour, newMin] = time.split(":").map(Number);
        const newStartMinutes = newHour * 60 + newMin;
        const newEndMinutes = newStartMinutes + (show.duration_minutes || 60);

        // Remove any existing booking that overlaps with new selection
        setBookingCart((prev) => {
          const filtered = prev.shows.filter((cartShow) => {
            if (cartShow.show.id !== show.id) return true; // Keep other shows

            const [cartHour, cartMin] = cartShow.time.split(":").map(Number);
            const cartStartMinutes = cartHour * 60 + cartMin;
            const cartEndMinutes = cartStartMinutes + (cartShow.show.duration_minutes || 60);

            // Check for overlap
            const hasOverlap =
              (newStartMinutes >= cartStartMinutes && newStartMinutes < cartEndMinutes) ||
              (newEndMinutes > cartStartMinutes && newEndMinutes <= cartEndMinutes) ||
              (newStartMinutes <= cartStartMinutes && newEndMinutes >= cartEndMinutes);

            if (hasOverlap) {
              // Remove from selected slots too
              const overlappingKey = `${cartShow.time}-${cartShow.show.id}-${cartShow.studio.id}`;
              newSelected.delete(overlappingKey);
              return false; // Remove this booking
            }
            return true; // Keep this booking
          });

          return {
            ...prev,
            shows: filtered,
          };
        });
      }

      // Add the main slot
      newSelected.add(key);

      // Don't add duration slots to selectedSlots - they should only show duration styling
      // Duration slots will be identified dynamically using isSlotInDuration

      // Calculate the price for this slot before calling onSlotSelect
      const slotPrice = getSlotPrice(show.id, time);

      // Get the pricing data efficiently
      const dayOfWeek = date.getDay();
      const showPriceData = priceData[show.id];
      let pricePointId = null;
      let userRoleId = null;
      let vatPercentage = null;
      let showTimeSlotPricingId = null;

      if (showPriceData && Array.isArray(showPriceData)) {
        const pricing = showPriceData.find((p: any) => {
          // Handle both HH:MM and HH:MM:SS formats
          const normalizedApiTime = p.time_slot.substring(0, 5); // Get just HH:MM
          return (
            normalizedApiTime === time &&
            (p.day_of_week === dayOfWeek || p.day_of_week === null)
          );
        });
        if (pricing) {
          pricePointId =
            pricing.price_point_id ||
            pricing.show_price_point_id ||
            pricing.pricing_id ||
            null;
          userRoleId = pricing.user_role_id || null;
          vatPercentage = pricing.vat_percentage || null;
          showTimeSlotPricingId = pricing.show_time_slot_pricing_id || null;
          console.log('Found pricing data for show:', {
            showId: show.id,
            showName: show.show_infos?.[0]?.name,
            pricePointId,
            time,
            pricing
          });
        } else {
          console.log('No pricing found in showPriceData for time slot:', {
            showId: show.id,
            time,
            dayOfWeek,
            availableSlots: showPriceData.map((p: any) => p.time_slot.substring(0, 5))
          });
        }
      } else {
        console.log('No price data available for show:', {
          showId: show.id,
          showName: show.show_infos?.[0]?.name,
          hasShowPriceData: !!showPriceData,
          isArray: Array.isArray(showPriceData)
        });
      }

      // Fallback to timeSlotPricing if needed
      if (!userRoleId) {
        const slotPricing = timeSlotPricing.find(
          (tp) =>
            tp.show_id === show.id &&
            tp.time_slot.startsWith(time) &&
            (tp.day_of_week === dayOfWeek || tp.day_of_week === null),
        );
        if (slotPricing) {
          userRoleId = slotPricing.user_role_id || null;
        }
      }

      // Override with staff role ID if we're in a staff portal
      if (isStaffPortal) {
        userRoleId = ROLES.BOOKING_STAFF;
      }

      // Update selectedSlots immediately and call onSlotSelect
      setSelectedSlots(newSelected);
      onSlotSelect(
        show,
        studio,
        time,
        slotPrice,
        pricePointId,
        userRoleId,
        vatPercentage,
        showTimeSlotPricingId,
        availableStudiosFromApi, // Pass available studios from API
      );
      setAnnouncement(`${showName} at ${time} has been selected`);
    }
  };

  const handleGapWarningCancel = () => {
    setGapWarningOpen(false);
    setGapWarningData(null);
  };

  // Show loading state when fetching shows
  if (isLoadingPrices && filteredShows.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-3"></div>
        <p className="text-muted-foreground">Loading available shows...</p>
      </div>
    );
  }

  // Only show "no shows" message after loading is complete
  if (!isLoadingPrices && filteredShows.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No shows available for this selection</p>
        <p className="text-xs mt-2">Try changing group size or booking type</p>
        <p className="text-xs text-muted-foreground">
          Debug: {filteredShows.length} filtered shows, {studios.length} studios
        </p>
      </div>
    );
  }

  return (
    <>
      <Card className="overflow-visible relative">
        <CardContent className="p-0 overflow-hidden relative">
          {(isLoadingBookings ||
            (isLoadingPrices && filteredShows.length > 0)) && (
            <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-30">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">
                  Loading {isLoadingBookings ? "bookings" : "prices"}...
                </p>
              </div>
            </div>
          )}
          {/* Screen reader announcements */}
          <div
            className="sr-only"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {announcement}
          </div>

          {timeSlots.length === 0 && !isLoadingBookings && !isLoadingPrices ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-lg font-medium">No time slots available</p>
              <p className="text-sm text-muted-foreground mt-1">
                There are no time slots configured for{" "}
                {date.toLocaleDateString("en-US", { weekday: "long" })}.
              </p>
            </div>
          ) : timeSlots.length > 0 ? (
            <>
              <div className="timeslot-grid-scroll overflow-auto -mx-6 px-6 max-h-[70vh] relative">
                <table
                  className="w-full table-auto"
                  role="table"
                  aria-label="Available booking times"
                >
                  <thead className="sticky top-0 z-30">
                    <tr className="border-b bg-background">
                      <th
                        className="text-left p-3 font-medium text-sm sticky left-0 bg-background z-40 border-r w-fit whitespace-nowrap"
                        scope="col"
                      >
                        Time
                      </th>
                      {filteredShows.map((show) => {
                        const showName =
                          show.show_infos?.find((info) => info.lang === "en")
                            ?.name ||
                          show.show_infos?.[0]?.name ||
                          `Show ${show.id}`;
                        const hasAssignedStudios =
                          show.show_studios && show.show_studios.length > 0;

                        return (
                          <th
                            key={show.id}
                            className="text-center p-3 font-medium text-sm min-w-[160px] bg-background"
                            scope="col"
                          >
                            {showName}
                            <div className="text-xs text-muted-foreground font-normal">
                              {isLoadingBookings || isLoadingPrices ? (
                                <>
                                  {show.min_participants}-
                                  {show.max_participants} people •{" "}
                                  {show.duration_minutes} min
                                </>
                              ) : hasAssignedStudios ? (
                                <>
                                  {show.min_participants}-
                                  {show.max_participants} people •{" "}
                                  {show.duration_minutes} min
                                </>
                              ) : (
                                <div className="text-red-600 font-medium mt-1">
                                  No studio assigned
                                </div>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time, idx) => (
                      <tr
                        key={time}
                        className={cn(
                          "border-b hover:bg-muted/30 transition-colors",
                          idx % 2 === 0 && "border-t-2", // Hourly divider
                        )}
                      >
                        <td
                          className="p-3 font-medium text-sm sticky left-0 bg-background z-10 border-r w-fit whitespace-nowrap"
                          scope="row"
                        >
                          <time dateTime={time}>{formatTimeDisplay(time, venueDefaultLanguage)}</time>
                        </td>
                        {filteredShows.map((show) => {
                          // Find the studio for this show from the cart
                          let studio = null;

                          // First, check if this show has assigned studios
                          const showHasAssignedStudios =
                            show.show_studios && show.show_studios.length > 0;

                          // If no studios assigned, render empty cell
                          if (!showHasAssignedStudios) {
                            return (
                              <td key={show.id} className="p-2 bg-gray-50"></td>
                            );
                          }

                          if (show.show_studios) {
                            let studioToUse = null;
                            const assignedStudios = show.show_studios.map((ss) => ss.studios);
                            const assignedStudioIds = new Set(assignedStudios.map((s) => s.id));

                            // IMPORTANT: First check if this show is in the cart for this time slot
                            // If so, use the studio from the cart (which may have been reassigned)
                            const cartEntry = bookingCart.shows.find(
                              (s) =>
                                s.show.id === show.id &&
                                s.time === time &&
                                s.date ===
                                  (date
                                    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                                    : "")
                            );

                            if (cartEntry && cartEntry.studio) {
                              // Use the studio from cart (may have been auto-reassigned).
                              // Fall back to the cart's studio object for road shows'
                              // virtual studio (id 0), which isn't in the venue list.
                              studioToUse =
                                studios.find((s) => s.id === cartEntry.studio.id) ||
                                cartEntry.studio ||
                                null;
                            } else {
                              // Not in cart, use the first assigned studio. Fall back to
                              // the assigned studio object so the road-show virtual studio
                              // (id 0, not in the venue list) still renders.
                              studioToUse =
                                studios.find((s) => s.id === assignedStudios[0].id) ||
                                assignedStudios[0] ||
                                null;
                            }

                            studio = studioToUse;
                          }

                          // For duration slots that are part of an existing cart booking, use the cart's studio
                          if (bookingCart.shows.length > 0 && !studio) {
                            // For duration slots, find the parent show that this slot belongs to
                            const [slotHour, slotMinute] = time
                              .split(":")
                              .map(Number);
                            const slotMinutes = slotHour * 60 + slotMinute;

                            // Find a cart show that this time slot could be a duration of
                            const parentShow = bookingCart.shows.find((s) => {
                              if (s.show.id !== show.id) return false;

                              const [startHour, startMinute] = s.time
                                .split(":")
                                .map(Number);
                              const startMinutes =
                                startHour * 60 + startMinute;
                              const duration = s.show.duration_minutes || 60;

                              // Check if this slot falls within the show's duration
                              return (
                                slotMinutes >= startMinutes &&
                                slotMinutes < startMinutes + duration
                              );
                            });

                            if (parentShow && parentShow.studio) {
                              const parentStudio = studios.find(
                                (s) => s.id === parentShow.studio.id,
                              );
                              if (parentStudio) {
                                studio = parentStudio;
                              }
                            }
                          }

                          if (!studio)
                            return (
                              <td
                                key={`${show.id}-${time}`}
                                className="p-2"
                              ></td>
                            );

                          const status = getSlotStatus(
                            show.id,
                            studio.id,
                            time,
                          );

                          const price = getSlotPrice(show.id, time);

                          // Check if this slot is selected - now checking the specific studio
                          const slotKey = `${time}-${show.id}-${studio.id}`;
                          const isSelected = selectedSlots.has(slotKey);

                          // Check if this slot has multiple overlapping shows
                          const overlappingShows = Array.from(selectedSlots)
                            .filter((slot) => slot.startsWith(`${time}-`))
                            .map((slot) => {
                              const [, showId, studioId] = slot.split("-");
                              return {
                                showId: parseInt(showId),
                                studioId: parseInt(studioId),
                              };
                            });

                          const hasMultipleShows = overlappingShows.length > 1;
                          const currentShowInOverlap = overlappingShows.find(
                            (s) =>
                              s.showId === show.id && s.studioId === studio.id,
                          );

                          // Check if multiple shows are available at this time slot (regardless of selection)

                          // A slot should appear selected only if it's directly selected
                          const shouldAppearSelected = isSelected;

                          // Debug selected slots
                          if (
                            (show.id === 1 && time === "07:00") ||
                            (show.id === 21 && time === "06:15")
                          ) {
                          }
                          // Road shows allow concurrent bookings (one per
                          // studio), so duration-based blocking must not apply —
                          // otherwise slots inside a prior booking's window can't
                          // be clicked. Availability comes purely from the
                          // remaining studios computed in getSlotStatus.
                          const isRoadShowFlow = bookingType === "roadshow";
                          const inDuration = isRoadShowFlow
                            ? false
                            : isSlotInDuration(time, show.id, studio.id);

                          // Check if this slot is part of a booked show's duration
                          const bookedDuration: {
                            inDuration: boolean;
                            startTime?: string;
                          } = isRoadShowFlow
                            ? { inDuration: false }
                            : isSlotInBookedDuration(time, show.id, studio.id);
                          const isAvailable =
                            (status === SLOT_STATUSES.AVAILABLE ||
                              status === SLOT_STATUSES.PARTIAL) &&
                            !inDuration &&
                            !bookedDuration.inDuration;
                          const isCommonSlot = isActuallyCommon(show.id, time);
                          const showName =
                            show.show_infos?.find((info) => info.lang === "en")
                              ?.name ||
                            show.show_infos?.[0]?.name ||
                            `Show ${show.id}`;

                          // Debug Show 2 at 6:00
                          if (show.id === 2 && time === "06:00") {
                          }

                          return (
                            <td
                              key={`${show.id}-${time}-${studio.id}`}
                              className="p-2 text-center"
                            >
                              <Button
                                variant={
                                  shouldAppearSelected
                                    ? "default"
                                    : isAvailable
                                      ? "outline"
                                      : "ghost"
                                }
                                size="sm"
                                className={cn(
                                  "w-full text-xs transition-colors relative",
                                  // Don't apply status-based cursor-not-allowed if slot is selected (user should be able to deselect)
                                  status === SLOT_STATUSES.SOLDOUT &&
                                    !shouldAppearSelected &&
                                    "bg-gray-900 text-white border-gray-900 opacity-90 cursor-not-allowed",
                                  status === SLOT_STATUSES.BOOKED &&
                                    !shouldAppearSelected &&
                                    "bg-blue-100 text-blue-900 border-blue-300 opacity-75 cursor-not-allowed",
                                  status === SLOT_STATUSES.UNAVAILABLE &&
                                    !shouldAppearSelected &&
                                    "bg-gray-200 text-gray-600 border-gray-400 opacity-80 cursor-not-allowed",
                                  status === SLOT_STATUSES.INSUFFICIENT_TIME &&
                                    !shouldAppearSelected &&
                                    "bg-purple-100 text-purple-900 border-purple-300 opacity-75 cursor-not-allowed",
                                  status === SLOT_STATUSES.PRELIMINARY &&
                                    !shouldAppearSelected &&
                                    "bg-red-100 text-red-900 border-red-300 opacity-75 cursor-not-allowed",
                                  status === SLOT_STATUSES.PARTIAL &&
                                    "bg-gray-100 text-gray-700 border-gray-300 opacity-90",
                                  status === SLOT_STATUSES.DURATION_BLOCKED &&
                                    !shouldAppearSelected &&
                                    "bg-blue-50 text-blue-700 border-blue-300 border-dashed border-2 cursor-not-allowed",
                                  status === SLOT_STATUSES.HOLIDAY_BLOCKED &&
                                    !shouldAppearSelected &&
                                    "bg-orange-100 text-orange-900 border-orange-300 opacity-75 cursor-not-allowed",
                                  price === null &&
                                    !shouldAppearSelected &&
                                    "bg-gray-100 text-gray-500 border-gray-300 opacity-75 cursor-not-allowed",
                                  inDuration &&
                                    !shouldAppearSelected &&
                                    "text-green-700 border-green-300 border-dotted border-2",
                                  bookedDuration.inDuration &&
                                    !shouldAppearSelected &&
                                    "bg-blue-50 text-blue-700 border-blue-300 border-dashed border-2 cursor-not-allowed",
                                  isAvailable &&
                                    !shouldAppearSelected &&
                                    price !== null &&
                                    "hover:bg-green-50 hover:border-green-600",
                                  shouldAppearSelected &&
                                    !hasMultipleShows &&
                                    "bg-green-600 hover:bg-green-700",
                                  shouldAppearSelected &&
                                    hasMultipleShows &&
                                    currentShowInOverlap &&
                                    "bg-green-600 hover:bg-green-700",
                                  isCommonSlot &&
                                    isAvailable &&
                                    !shouldAppearSelected &&
                                    "border-2 border-primary shadow-sm",
                                )}
                                disabled={
                                  // Allow clicking on selected slots to deselect them
                                  shouldAppearSelected
                                    ? false
                                    : !isAvailable ||
                                      price === null ||
                                      inDuration ||
                                      bookedDuration.inDuration
                                }
                                onClick={async () => {
                                  // Get available studios from the data we already fetched
                                  let availableStudiosForCart: any[] | null = null;

                                  const timeWithSeconds = time + ":00";
                                  const currentSlotData = allSlotData.find(
                                    (s: any) => s.time_slot === timeWithSeconds && s.show_id === show.id
                                  );

                                  if (bookingType === "roadshow") {
                                    // Place the booking on a studio that is
                                    // actually free at this time (same overlap
                                    // logic as the slot status). getSlotStatus
                                    // already guarantees at least one free studio
                                    // for a clickable road-show slot.
                                    const assignedStudioObjs = (show.show_studios || [])
                                      .map((ss) => ss.studios)
                                      .filter(Boolean) as any[];
                                    const { freeIds } =
                                      getRoadShowStudioAvailability(show.id, time);
                                    const freeAssigned = assignedStudioObjs.filter(
                                      (s) => freeIds.includes(s.id),
                                    );
                                    availableStudiosForCart =
                                      freeAssigned.length > 0
                                        ? freeAssigned
                                        : assignedStudioObjs;
                                  } else if (currentSlotData) {
                                    const { available_studio_ids } = currentSlotData;

                                    // Check if no studios are available (considering cart items)
                                    // We need to consider studios already in cart for this time slot
                                    const studiosInCartAtTime = bookingCart.shows
                                      .filter(item =>
                                        item.date === `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` &&
                                        item.time === time
                                      )
                                      .map(item => item.studio.id);

                                    // Filter out studios already in cart from available studios
                                    const actuallyAvailable = available_studio_ids?.filter(
                                      (id: number) => !studiosInCartAtTime.includes(id)
                                    ) || [];

                                    if (!bookingId && actuallyAvailable.length === 0 && !bookingCart.shows.some(s => s.show.id === show.id && s.time === time)) {
                                      toast.error("Cannot book this time: all studios are occupied. Please select a different time.");
                                      return;
                                    }

                                    // Get studio objects for available studios.
                                    // Resolve from the show's OWN assigned studios
                                    // first — road-show studios may not exist in
                                    // the venue `studios` list, which otherwise
                                    // leaves this empty and falls back to the
                                    // (possibly booked) clicked studio.
                                    const assignedStudioObjs = (show.show_studios || [])
                                      .map((ss) => ss.studios)
                                      .filter(Boolean) as any[];
                                    const assignedStudiosAvailable = assignedStudioObjs.filter(
                                      (s) => actuallyAvailable.includes(s.id),
                                    );

                                    // If assigned studios available, use those; otherwise use all available
                                    availableStudiosForCart = assignedStudiosAvailable.length > 0
                                      ? assignedStudiosAvailable
                                      : studios.filter((s) => actuallyAvailable.includes(s.id));
                                  }

                                  // Pass available studios to handleSlotClick for conflict resolution
                                  await handleSlotClick(show, studio, time, status, availableStudiosForCart);
                                }}

                                aria-label={`${showName} at ${time}, ${
                                  inDuration && !isSelected
                                    ? "part of show duration from " +
                                      getShowStartTime(time, show.id, studio.id)
                                    : bookedDuration.inDuration
                                      ? "part of booking duration from " +
                                        bookedDuration.startTime
                                      : status === SLOT_STATUSES.HOLIDAY_BLOCKED
                                        ? "blocked due to holiday or maintenance"
                                        : status === SLOT_STATUSES.SOLDOUT
                                          ? "sold out"
                                          : status === SLOT_STATUSES.BOOKED
                                            ? "booked"
                                            : status === SLOT_STATUSES.UNAVAILABLE
                                              ? "unavailable - studio occupied by other show"
                                            : status === SLOT_STATUSES.INSUFFICIENT_TIME
                                              ? "insufficient time - would overlap with existing bookings"
                                            : status === SLOT_STATUSES.PARTIAL
                                              ? "partially booked"
                                              : status ===
                                                  SLOT_STATUSES.DURATION_BLOCKED
                                                ? "blocked: part of booked show duration"
                                                : price !== null
                                                  ? `${price} ${getCurrencySymbol(venueCurrency)}, ${shouldAppearSelected ? "selected" : "click to select"}`
                                                  : "price unavailable"
                                }${isCommonSlot ? " (Available for both guest and staff)" : ""}`}
                                aria-pressed={shouldAppearSelected}
                              >
                                {hasMultipleShows &&
                                currentShowInOverlap &&
                                shouldAppearSelected ? (
                                  getShowStartTime(time, show.id, studio.id) ===
                                    time && price !== null ? (
                                    <span>
                                      {price} {getCurrencySymbol(venueCurrency)}
                                    </span>
                                  ) : (
                                    <span className="text-xs">
                                      {getShowStartTime(
                                        time,
                                        show.id,
                                        studio.id,
                                      )}{" "}
                                      show
                                    </span>
                                  )
                                ) : shouldAppearSelected &&
                                  !hasMultipleShows ? (
                                  // For selected slots without multiple shows
                                  getShowStartTime(time, show.id, studio.id) ===
                                    time && price !== null ? (
                                    <span>
                                      {price} {getCurrencySymbol(venueCurrency)}
                                    </span>
                                  ) : (
                                    <span className="text-[10px]">
                                      {getShowStartTime(
                                        time,
                                        show.id,
                                        studio.id,
                                      )}{" "}
                                      show
                                    </span>
                                  )
                                ) : inDuration && !shouldAppearSelected ? (
                                  (() => {
                                    const showStartTime = getShowStartTime(time, show.id, studio.id);
                                    // Fallback: find earliest selected slot for this show if getShowStartTime returns null
                                    const fallbackTime = showStartTime || (() => {
                                      let earliest = null;
                                      for (const selected of selectedSlots) {
                                        const [selTime, selShowId] = selected.split("-");
                                        if (parseInt(selShowId) === show.id) {
                                          if (!earliest || selTime < earliest) {
                                            earliest = selTime;
                                          }
                                        }
                                      }
                                      return earliest;
                                    })();
                                    return (
                                      <>
                                        <span className="flex items-center gap-1 text-[10px]">
                                          <span className="inline-block w-2 h-2 bg-green-600 rounded-full animate-pulse"></span>
                                          {fallbackTime} show
                                        </span>
                                      </>
                                    );
                                  })()
                                ) : status === SLOT_STATUSES.UNAVAILABLE ? (
                                  <span className="font-semibold">UNAVAILABLE</span>
                                ) : status === SLOT_STATUSES.INSUFFICIENT_TIME ? (
                                  <span className="font-semibold text-xs">INSUFFICIENT TIME</span>
                                ) : bookedDuration.inDuration ? (
                                  <>
                                    <span className="flex items-center gap-1 text-[10px]">
                                      <span className="inline-block w-2 h-2 bg-blue-600 rounded-full"></span>
                                      {bookedDuration.startTime} booking
                                    </span>
                                  </>
                                ) : status === SLOT_STATUSES.HOLIDAY_BLOCKED ? (
                                  <span className="font-semibold">BLOCKED</span>
                                ) : status === SLOT_STATUSES.SOLDOUT ? (
                                  <span className="font-semibold">
                                    SOLD OUT
                                  </span>
                                ) : status === SLOT_STATUSES.BOOKED ? (
                                  <span>BOOKED</span>
                                ) : status === SLOT_STATUSES.PRELIMINARY ? (
                                  <span className="text-[10px]">
                                    Preliminary: Temporarily held
                                  </span>
                                ) : status === SLOT_STATUSES.PARTIAL ? (
                                  // For partial slots, show price if available, otherwise show "PARTIAL"
                                  price !== null ? (
                                    <span>
                                      {price} {getCurrencySymbol(venueCurrency)}
                                    </span>
                                  ) : (
                                    <span>PARTIAL</span>
                                  )
                                ) : status ===
                                  SLOT_STATUSES.DURATION_BLOCKED ? (
                                  <span className="flex items-center gap-1 text-[10px]">
                                    <span className="inline-block w-2 h-2 bg-blue-600 rounded-full"></span>
                                    {/* Need to find the booking start time for this duration slot */}
                                    {(() => {
                                      // Find the booking that this slot is part of
                                      const [slotHour, slotMinute] = time
                                        .split(":")
                                        .map(Number);
                                      const slotMinutes =
                                        slotHour * 60 + slotMinute;
                                      const showDuration =
                                        show.duration_minutes || 60;

                                      for (const booking of bookings) {
                                        const bookingPricingSlot =
                                          timeSlotPricing.find(
                                            (tp) =>
                                              tp.id ===
                                              booking.show_time_slot_pricing_id,
                                          );

                                        // Match by pricing row when available,
                                        // otherwise fall back to the booking's
                                        // own show_id — road-show pricing rows
                                        // aren't always present in timeSlotPricing.
                                        if (
                                          (bookingPricingSlot &&
                                            bookingPricingSlot.show_id ===
                                              show.id) ||
                                          booking.show_id === show.id
                                        ) {
                                          const bookingTime =
                                            booking.time_slot.substring(0, 5);
                                          const [bookingHour, bookingMinute] =
                                            bookingTime.split(":").map(Number);
                                          const bookingMinutes =
                                            bookingHour * 60 + bookingMinute;

                                          if (
                                            slotMinutes > bookingMinutes &&
                                            slotMinutes <
                                              bookingMinutes + showDuration
                                          ) {
                                            return `${bookingTime} show`;
                                          }
                                        }
                                      }
                                      return "show";
                                    })()}
                                  </span>
                                ) : price !== null ? (
                                  <span>
                                    {price} {getCurrencySymbol(venueCurrency)}
                                  </span>
                                ) : (
                                  <span>No price</span>
                                )}
                              </Button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="p-3 bg-muted/30 border-t">
                <div className="flex flex-wrap items-center gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-600 rounded" />
                    <span>
                      <strong>Selected:</strong> Your chosen time slot
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-300 rounded bg-white" />
                    <span>
                      <strong>Available:</strong> Click to book this time
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary rounded bg-white shadow-sm" />
                    <span>
                      <strong>Recommended</strong> Available for both guest and
                      staff
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-white border-2 border-dotted border-green-300 rounded relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full"></div>
                      </div>
                    </div>
                    <span>
                      <strong>Duration:</strong> Part of selected show time
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-50 border-2 border-dashed border-blue-300 rounded relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                      </div>
                    </div>
                    <span>
                      <strong>Blocked:</strong> Part of booked show duration
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-900 rounded opacity-90" />
                    <span>
                      <strong>Sold Out:</strong> No more spots available
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded opacity-75" />
                    <span>
                      <strong>Booked:</strong> All studios booked
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 border border-gray-400 rounded opacity-80" />
                    <span>
                      <strong>Unavailable:</strong> Studio occupied by other show
                    </span>
                  </div>
                  {isStaffPortal && (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-purple-100 border border-purple-300 rounded opacity-75" />
                      <span>
                        <strong>Insufficient Time:</strong> Would overlap with existing bookings
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded opacity-90" />
                    <span>
                      <strong>Partial:</strong> Some studios available
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-100 border border-red-300 rounded opacity-75" />
                    <span>
                      <strong>Preliminary:</strong> Temporarily held
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded opacity-75" />
                    <span>
                      <strong>Blocked:</strong> Holiday/Maintenance
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Loading state - show empty div while data loads
            <div className="py-8"></div>
          )}
        </CardContent>
      </Card>

      {/* Gap Warning Modal */}
      <BookingGapWarningModal
        isOpen={gapWarningOpen}
        conflictingShow={gapWarningData || undefined}
        onCancel={handleGapWarningCancel}
      />
    </>
  );
}
